import React from 'react'
import { capitalCase } from 'change-case'
import { usePlatformPlacementStyles } from './styles'
import { PublisherPlatformsMode } from './publisher-platforms.types'
import { placementLabel } from './placement-labels'
import {
  PlacementPositions,
  PlacementPositionKey,
  platformByPositionKey,
  PublisherPlatform,
  publisherPlatforms,
  sendablePlacementPositions,
  placementPositionKeys,
} from '@marketing-milk/interfaces'
import {
  Checkbox,
  RadioGroup,
  Radio,
  FormControl,
  FormControlLabel,
  Container,
} from '@material-ui/core'

type PublisherPlatformsProps = {
  handleModeChange: (mode: PublisherPlatformsMode) => void
  handlePlatformSelection: (platform: PublisherPlatform) => void
  handlePositionSelection: (key: PlacementPositionKey, position: string) => void
  selectedPlatforms?: PublisherPlatform[]
  selectedPositions?: PlacementPositions
}

export const PublisherPlatforms = ({
  handleModeChange,
  handlePlatformSelection,
  handlePositionSelection,
  selectedPlatforms,
  selectedPositions,
}: PublisherPlatformsProps) => {
  const classes = usePlatformPlacementStyles()

  const _handlePlatformSelection = (e: React.ChangeEvent<HTMLInputElement>, checked: Boolean) => {
    // Return early if user is trying to unselect the last checkbox. At least one must be selected.
    if (selectedPlatforms?.length === 1 && selectedPlatforms[0] === e.target.value) return
    handlePlatformSelection(e.target.value as PublisherPlatform)
  }

  return (
    <div className={classes.platformPlacements} data-testid="publisher-platforms">
      <div className={'col ' + classes.placementOptions}>
        <FormControl component="fieldset">
          <RadioGroup
            name="placements"
            value={selectedPlatforms ? 'manual' : 'automatic'}
            onChange={(_, value) => handleModeChange(value as PublisherPlatformsMode)}
          >
            <FormControlLabel
              value="automatic"
              control={<Radio color="primary" />}
              label="Automatically Assign Platforms (Recommended)"
            />
            <p className="ml-2">
              Maximize your budget and help show your ads to more people, our system will allocate
              your ad set's budget across multiple platforms based on where they're likely to
              perform best.
            </p>
            <FormControlLabel
              value="manual"
              control={<Radio color="primary" />}
              label="Manually Assign Platforms"
            />
            <p className="ml-2 ">
              Manually choose the platforms to show your ad. The more platforms you select, the more
              oppurtunities you'll have to reach your target audience and achieve your business
              goals
            </p>
          </RadioGroup>
        </FormControl>
      </div>
      {selectedPlatforms && (
        <Container hidden={!selectedPlatforms} className={classes.possiblePlatforms}>
          <h5>Choose Platforms</h5>
          <div className={classes.platformCheckboxes}>
            {publisherPlatforms.map(p => {
              const prettyName = capitalCase(p)
              return (
                <FormControlLabel
                  key={p}
                  htmlFor={p}
                  label={prettyName}
                  role="checkbox"
                  control={
                    <Checkbox
                      value={p}
                      checked={selectedPlatforms.includes(p)}
                      inputProps={{ 'aria-checked': selectedPlatforms.includes(p) }}
                      onChange={_handlePlatformSelection}
                    />
                  }
                />
              )
            })}
          </div>

          <h5 className="mt-4">Choose Placements</h5>
          {placementPositionKeys
            .filter(key => selectedPlatforms.includes(platformByPositionKey[key]))
            .map(key => (
              <div key={key} className="mb-2">
                <h6>{capitalCase(platformByPositionKey[key])}</h6>
                <div className={classes.platformCheckboxes}>
                  {sendablePlacementPositions[key].map(position => {
                    // an absent key means every position on the platform is selected
                    const checked = (selectedPositions?.[key] as string[] | undefined)
                      ? (selectedPositions?.[key] as string[]).includes(position)
                      : true
                    return (
                      <FormControlLabel
                        key={position}
                        htmlFor={position}
                        label={placementLabel(position)}
                        role="checkbox"
                        control={
                          <Checkbox
                            value={position}
                            checked={checked}
                            inputProps={{ 'aria-checked': checked }}
                            onChange={() => handlePositionSelection(key, position)}
                          />
                        }
                      />
                    )
                  })}
                </div>
              </div>
            ))}
        </Container>
      )}
    </div>
  )
}
